import { Box, Checkbox, FormControlLabel, FormLabel, Grid, OutlinedInput, Stack, Typography } from '@mui/material';
import { DrawerHeader } from '../../drawer/DrawerHeader';
import { FormField } from '../../form/FormField';
import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { useTheme } from '@mui/material/styles';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { uniqBy } from 'lodash';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { useDebounceEffect } from '../../utils/useDebounceEffect';
import { Editor } from '../../components/wysiwyg/Editor';
import { DrawerWrapper } from '../../drawer/DrawerWrapper';
import { useSnackbar } from '../../store/snackbar';

const getPhaseQuery = graphql(`
	query phaseEditDrawer_getPhase($phaseId: UUID!) {
		getPhase(by: { id: $phaseId }) {
			id
			name
			description
			touchpoints {
				activity {
					contentMetrics {
						id
						metric {
							name
						}
					}
				}
			}
			contentMetrics {
				id
				metric {
					name
				}
			}
		}
	}
`);

const updatePhaseMutation = graphql(`
	mutation editPhase_update(
		$phaseId: UUID!
		$name: String!
		$description: String
		$contentMetrics: [PhaseUpdateContentMetricsEntityRelationInput!]
	) {
		updatePhase(by: { id: $phaseId }, data: { name: $name, description: $description, contentMetrics: $contentMetrics }) {
			ok
			errorMessage
		}
	}
`);

export const phaseEditDrawerSchema = z.object({
	phaseId: z.string(),
});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().min(1, intl.formatMessage({ id: 'form.edit-phase.error.empty-name' })),
		description: z.string().nullable(),
	});

export const PhaseEditDrawer: FC<z.infer<typeof phaseEditDrawerSchema>> = ({ phaseId }) => {
	const intl = useIntl();
	const theme = useTheme();
	const [isEditorReady, setIsEditorReady] = useState(false);
	const { openSnackbar } = useSnackbar();

	const { data, isPending } = useApiQuery(getPhaseQuery, {
		variables: { phaseId },
	});

	const { mutate } = useApiMutation(updatePhaseMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updatePhase.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'drawer.edit-phase.update.success' }),
					error: intl.formatMessage({ id: 'drawer.edit-phase.update.error' }),
				},
				errorMessage: result.updatePhase.errorMessage,
				openSnackbar,
			}),
	});

	const form = useTypedForm({
		schema: createFormSchema(intl),
		mode: 'all',
		reValidateMode: 'onChange',
	});

	const metricHandle = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { checked, value } = event.target;
		mutate({
			phaseId,
			name,
			contentMetrics: [
				{
					[checked ? 'connect' : 'disconnect']: {
						id: value,
					},
				},
			],
		});
	};

	useEffect(() => {
		if (!isPending) {
			form.reset({
				name: data?.getPhase?.name ?? '',
				description: data?.getPhase?.description ?? null,
			});
			setIsEditorReady(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPending]);

	const { name, description } = form.watch();
	const { isValid, isDirty } = form.formState;

	useDebounceEffect(() => {
		if (isEditorReady && ((isValid && isDirty) || data?.getPhase?.description !== description)) {
			mutate({ phaseId, description, name });
		}
	}, [name, description, isValid, phaseId, mutate, data, isDirty, isEditorReady]);

	const activityMetrics = data?.getPhase?.touchpoints.flatMap((touchpoint) => {
		return touchpoint.activity?.contentMetrics;
	});

	const uniqueMetrics = uniqBy(activityMetrics, (activity) => activity?.id);

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'drawer.edit-phase.title' })} />
			<Box width={'100%'}>
				<form noValidate>
					<Stack gap={2}>
						<FormField
							form={form}
							name={'name'}
							label={intl.formatMessage({ id: 'drawer.edit-phase.label.name' })}
							render={(props) => <OutlinedInput {...props} />}
						/>
						<Stack gap={1}>
							<FormLabel>
								<Typography color={theme.palette.grey[600]}>
									<FormattedMessage id={'drawer.edit-phase.label.description'} />
								</Typography>
							</FormLabel>
							<Box width={'100%'}>
								{isEditorReady && (
									<Editor placeholder={'...'} state={description} setState={(value) => form.setValue('description', value)} />
								)}
							</Box>
						</Stack>

						{uniqueMetrics.length > 0 && (
							<Stack>
								<FormLabel>
									<Typography color={theme.palette.grey[600]}>
										<FormattedMessage id={'drawer.edit-journey.label.summaryDisplay'} />
									</Typography>
								</FormLabel>
								<Grid container>
									{uniqueMetrics.map(
										(item) =>
											item && (
												<Grid item xs={6} key={item.id}>
													<Stack direction={'row'} alignItems={'center'}>
														<FormControlLabel
															value={item.id}
															control={<Checkbox checked={data?.getPhase?.contentMetrics.some((obj) => obj.id === item.id)} />}
															label={item.metric?.name}
															onChange={(event) => metricHandle(event as ChangeEvent<HTMLInputElement>)}
														/>
													</Stack>
												</Grid>
											),
									)}
								</Grid>
							</Stack>
						)}
					</Stack>
				</form>
			</Box>
		</DrawerWrapper>
	);
};
